import clsx from 'clsx'
import {FC, useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {KTSVG} from '../../../../_gori/helpers'
import {ShipmentTrendPoint} from '../core/_types'
import {
  OrganizationAnalyticsFilters,
  ShipmentTrendBreakout,
} from '../helpers/organizationAnalyticsHelpers'
import {SHIPMENT_TREND_BREAKOUTS, TREND_VIEW_LABEL_KEYS} from '../core/_const'
import {createShipmentTrendsChart} from '../helpers/shipmentTrendsChartHelpers'
import {useShipmentTrendData} from '../hooks/useShipmentTrendData'
import {ShipmentTrendsLegend} from './ShipmentTrendsLegend'

export type {ShipmentTrendPoint}

type Props = OrganizationAnalyticsFilters & {
  title?: string
  className?: string
  onExpand?: () => void
}

const ShipmentTrends: FC<Props> = ({
  title,
  className,
  onExpand,
  accountIds,
  timeRange,
  isCustomRange,
  dateRange,
}) => {
  const intl = useIntl()
  const chartRef = useRef<HTMLDivElement | null>(null)
  const chartIdRef = useRef(`shipment-trends-chart-${Math.random().toString(36).slice(2, 11)}`)
  const chartId = chartIdRef.current
  const [activeView, setActiveView] = useState<ShipmentTrendBreakout>('overall')
  const analyticsFilters = useMemo(
    () => ({accountIds, timeRange, isCustomRange, dateRange}),
    [accountIds, dateRange, isCustomRange, timeRange]
  )

  const {trendData, seriesTrendData, hasChartData, isLoading} = useShipmentTrendData(
    activeView,
    analyticsFilters
  )

  const showChartSkeleton = isLoading && !hasChartData

  const chartTitle = title ?? intl.formatMessage({id: 'SHIPMENT_TRENDS'})
  const shipmentsLabel = intl.formatMessage({id: 'SHIPMENTS'})
  const trendViews = useMemo(
    () =>
      SHIPMENT_TREND_BREAKOUTS.map((id) => ({
        id,
        label: intl.formatMessage({id: TREND_VIEW_LABEL_KEYS[id]}),
      })),
    [intl]
  )

  useEffect(() => {
    if (!chartRef.current || !hasChartData || isLoading) {
      return
    }

    return createShipmentTrendsChart({
      chartId,
      activeView,
      overallData: trendData.overall,
      seriesData: seriesTrendData,
      shipmentsLabel,
      timeRange,
      isCustomRange,
      dateRange,
    })
  }, [
    activeView,
    chartId,
    dateRange,
    hasChartData,
    isCustomRange,
    isLoading,
    seriesTrendData,
    shipmentsLabel,
    timeRange,
    trendData.overall,
  ])

  return (
    <div className={clsx('card shadow-sm shipment-trends', className)}>
      <div className='card-body shipment-trends__body'>
        <div className='shipment-trends__header'>
          <h3 className='shipment-trends__title mb-0'>{chartTitle}</h3>
          <div className='shipment-trends__controls'>
            <div className='shipment-trends__segmented' role='group' aria-label='Trend view'>
              {trendViews.map((view) => (
                <button
                  key={view.id}
                  type='button'
                  className={clsx('shipment-trends__segment', {
                    'shipment-trends__segment--active': activeView === view.id,
                  })}
                  onClick={() => setActiveView(view.id)}
                >
                  {view.label}
                </button>
              ))}
            </div>
            {onExpand && (
              <button
                type='button'
                className='shipment-trends__expand-btn'
                aria-label='Expand chart'
                onClick={onExpand}
              >
                <KTSVG path='media/gori/organizations/expand.svg' svgClassName='' />
              </button>
            )}
          </div>
        </div>

        <div ref={chartRef} id={chartId} className='shipment-trends__chart w-100'>
          {showChartSkeleton && (
            <div className='organizations-overview-skeleton__trends-chart placeholder-wave' />
          )}
        </div>
        {!showChartSkeleton && (
          <div className='d-flex justify-content-end' key={activeView}>
            <ShipmentTrendsLegend
              activeView={activeView}
              seriesTrendData={seriesTrendData}
              shipmentsLabel={shipmentsLabel}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export {ShipmentTrends}
